import { mockOrders } from "../constants/mockData";

const scheduleRank = {
  CHANGE_REVIEW: 1,
  CHECK_REQUIRED: 2,
  PLAN_REVIEW: 3,
  HOLD: 4,
  WAITING: 5,
  CONFIRMED: 6,
  COMPLETED: 7,
  CANCELLED: 8,
};

const riskRank = {
  OVERDUE: 1,
  HIGH: 2,
  ATTENTION: 3,
  NORMAL: 4,
};

const minDate = (values = []) => values.filter(Boolean).sort()[0] || null;

const minNumber = (values = []) => {
  const numbers = values.map(Number).filter(Number.isFinite);
  return numbers.length ? Math.min(...numbers) : null;
};

const countValue = (items, key, value) =>
  items.reduce((sum, item) => sum + (String(item?.[key] || "") === value ? 1 : 0), 0);

const pickByRank = (items, key, ranks, fallback) =>
  [...items]
    .map((item) => item?.[key] || fallback)
    .sort((a, b) => (ranks[a] || 99) - (ranks[b] || 99))[0] || fallback;

const buildCollapsedOrderRow = (items = []) => {
  const sorted = [...items].sort((a, b) => Number(a.line_no || 0) - Number(b.line_no || 0));
  const first = sorted[0] || {};
  const unitCodes = [...new Set(sorted.map((item) => item.unit_code).filter(Boolean))];
  const lineCount = sorted.reduce((sum, item) => sum + Number(item.line_count || 1), 0);
  const sameUnit = unitCodes.length <= 1;
  const quantitySum = sorted.reduce((sum, item) => sum + (Number(item.order_quantity) || 0), 0);

  return {
    ...first,
    order_line_id: first.order_line_id,
    line_count: lineCount || 1,
    order_quantity: sameUnit ? quantitySum : null,
    total_order_quantity: quantitySum,
    unit_code: sameUnit ? unitCodes[0] || first.unit_code : "MIXED",
    requested_due_date: minDate(sorted.map((item) => item.requested_due_date)) || first.requested_due_date,
    required_start_date: minDate(sorted.map((item) => item.required_start_date)),
    check_required_date: minDate(sorted.map((item) => item.check_required_date)),
    available_margin_days: minNumber(sorted.map((item) => item.available_margin_days)),
    schedule_status: pickByRank(sorted, "schedule_status", scheduleRank, "WAITING"),
    risk_level: pickByRank(sorted, "risk_level", riskRank, "NORMAL"),
    schedule_change_review_count: sorted.reduce((sum, item) => sum + Number(item.schedule_change_review_count || 0), 0) || countValue(sorted, "schedule_status", "CHANGE_REVIEW"),
    schedule_check_required_count: sorted.reduce((sum, item) => sum + Number(item.schedule_check_required_count || 0), 0) || countValue(sorted, "schedule_status", "CHECK_REQUIRED"),
    schedule_plan_review_count: sorted.reduce((sum, item) => sum + Number(item.schedule_plan_review_count || 0), 0) || countValue(sorted, "schedule_status", "PLAN_REVIEW"),
    schedule_hold_count: sorted.reduce((sum, item) => sum + Number(item.schedule_hold_count || 0), 0) || countValue(sorted, "schedule_status", "HOLD"),
    schedule_waiting_count: sorted.reduce((sum, item) => sum + Number(item.schedule_waiting_count || 0), 0) || countValue(sorted, "schedule_status", "WAITING"),
    risk_overdue_count: sorted.reduce((sum, item) => sum + Number(item.risk_overdue_count || 0), 0) || countValue(sorted, "risk_level", "OVERDUE"),
    risk_high_count: sorted.reduce((sum, item) => sum + Number(item.risk_high_count || 0), 0) || countValue(sorted, "risk_level", "HIGH"),
    risk_attention_count: sorted.reduce((sum, item) => sum + Number(item.risk_attention_count || 0), 0) || countValue(sorted, "risk_level", "ATTENTION"),
  };
};

export const collapseRowsByOrderNo = (items = []) => {
  const grouped = items.reduce((acc, row) => {
    const key = String(row?.order_no || row?.order_id || row?.order_line_id || "");
    if (!key) return acc;
    acc[key] = acc[key] || [];
    acc[key].push(row);
    return acc;
  }, {});

  return Object.values(grouped).map(buildCollapsedOrderRow);
};

export const statusOptions = [
  ["", "전체 상태"],
  ["WAITING", "일정 대기"],
  ["CHECK_REQUIRED", "확인 필요"],
  ["CONFIRMED", "계획 확정"],
  ["CHANGE_REVIEW", "변경 검토"],
  ["PLAN_REVIEW", "계획 검토"],
  ["HOLD", "보류"],
  ["COMPLETED", "완료"],
];

export const riskOptions = [
  ["", "전체 위험도"],
  ["NORMAL", "정상"],
  ["ATTENTION", "주의"],
  ["HIGH", "고위험"],
  ["OVERDUE", "기한 초과"],
  ["DELIVERY_RISK", "납기 위험"],
];

export const isEmptyOrders = (value) =>
  !value?.items?.length && Number(value?.pagination?.total || 0) === 0;

export const getOrderViewData = (data, filters) => {
  if (!isEmptyOrders(data)) {
    const sourceItems = data.items || [];
    const orderKeys = sourceItems.map((row) => String(row?.order_no || row?.order_id || row?.order_line_id || ""));
    const hasDuplicateOrder = new Set(orderKeys).size < orderKeys.length;
    const items = collapseRowsByOrderNo(sourceItems);
    return {
      ...data,
      items,
      pagination: {
        ...data.pagination,
        total: hasDuplicateOrder ? items.length : Number(data.pagination?.total || items.length),
        totalPages: hasDuplicateOrder
          ? Math.max(1, Math.ceil(items.length / Number(data.pagination?.pageSize || items.length || 1)))
          : Number(data.pagination?.totalPages || 1),
      },
    };
  }

  const keyword = filters.search.trim().toLowerCase();
  const filtered = mockOrders.filter((row) => {
    const matchesStatus = !filters.status || row.schedule_status === filters.status;
    const matchesRisk = !filters.riskLevel ||
      (filters.riskLevel === "DELIVERY_RISK"
        ? ["HIGH", "OVERDUE"].includes(row.risk_level)
        : row.risk_level === filters.riskLevel);
    const target = [
      row.order_no,
      row.customer_po_no,
      row.customer_name,
      row.site_name,
      row.item_code_snapshot,
      row.item_name_snapshot,
      row.specification_snapshot,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return matchesStatus && matchesRisk && (!keyword || target.includes(keyword));
  });

  const items = collapseRowsByOrderNo(filtered);

  return {
    items,
    pagination: {
      page: 1,
      total: items.length,
      totalPages: 1,
    },
  };
};

export const getOrderSummary = (items) =>
  items.reduce(
    (acc, row) => {
      acc.total += 1;
      if (row.schedule_status === "CHECK_REQUIRED") acc.checkRequired += 1;
      if (row.schedule_status === "CHANGE_REVIEW") acc.changeReview += 1;
      if (row.risk_level === "HIGH" || row.risk_level === "OVERDUE") acc.deliveryRisk += 1;
      return acc;
    },
    { total: 0, checkRequired: 0, changeReview: 0, deliveryRisk: 0 }
  );
